import { fetchAPI } from './fetchAPI';

// ==================== Evaluation Metrics ====================

export const getEvaluationMetrics = async (days: number | null = null, startDate: string | null = null, endDate: string | null = null): Promise<any> => {
  const queryParams = new URLSearchParams();

  // Explicit date range takes priority
  if (startDate && endDate) {
    queryParams.append('start_date', startDate);
    queryParams.append('end_date', endDate);
  } else if (days) {
    queryParams.append('days', String(days));
  }
  // Note: When no date params are provided, backend returns all-time data

  const query = queryParams.toString();
  return fetchAPI(`/genaiops/evaluations/metrics${query ? '?' + query : ''}`);
};

// ==================== Evaluation Engine Endpoints ====================

export const runEvaluation = async (params: Record<string, any>): Promise<any> => {
  const body: Record<string, any> = {
    dataset_id: params.datasetId,
    prompt_id: params.promptId,
    prompt_template: params.promptTemplate,
    provider: params.provider || 'openai',
    model_id: params.modelId || 'gpt-4o-mini',
    credential_id: params.credentialId,
    scoring_method: params.scoringMethod || 'contains',
    pass_threshold: params.passThreshold || 0.7,
    quality_gate: params.qualityGate,
  };

  // LLM Judge options
  if (params.judgeRubricId) {
    body.judge_rubric_id = params.judgeRubricId;
  }

  // Semantic similarity options
  if (params.scoringMethod === 'semantic_similarity') {
    if (params.embeddingProvider) body.embedding_provider = params.embeddingProvider;
    if (params.embeddingModelId) body.embedding_model_id = params.embeddingModelId;
    if (params.similarityThreshold) body.similarity_threshold = params.similarityThreshold;
  }

  // Safety testing options
  if (params.includeSafety) {
    body.include_safety = true;
    if (params.safetyFailThreshold) body.safety_fail_threshold = params.safetyFailThreshold;
    if (params.safetyTests) body.safety_tests = params.safetyTests;
  }

  // Assertion options
  if (params.runAssertions !== undefined) {
    body.run_assertions = params.runAssertions;
  }

  // BERTScore options
  if (params.scoringMethod === 'bert_score') {
    if (params.bertModelType) body.bert_model_type = params.bertModelType;
    if (params.bertThreshold) body.bert_threshold = params.bertThreshold;
    if (params.bertUseIdf !== undefined) body.bert_use_idf = params.bertUseIdf;
  }

  // ROUGE options
  if (params.scoringMethod === 'rouge') {
    if (params.rougePrimaryMetric) body.rouge_primary_metric = params.rougePrimaryMetric;
    if (params.rougeThreshold) body.rouge_threshold = params.rougeThreshold;
    if (params.rougeUseStemmer !== undefined) body.rouge_use_stemmer = params.rougeUseStemmer;
  }

  // AWS Bedrock Evaluation options
  if (params.scoringMethod === 'bedrock_evaluation') {
    if (params.bedrockTaskType) body.bedrock_task_type = params.bedrockTaskType;
    if (params.bedrockEvaluationMetrics) body.bedrock_evaluation_metrics = params.bedrockEvaluationMetrics;
    if (params.bedrockTimeoutMinutes) body.bedrock_timeout_minutes = params.bedrockTimeoutMinutes;
  }

  // Secondary metrics
  if (params.calculateSecondaryMetrics && (params.secondaryMetrics?.length ?? 0) > 0) {
    body.calculate_secondary_metrics = true;
    body.secondary_metrics = params.secondaryMetrics;
  }

  return fetchAPI('/genaiops/evaluations/run', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export const getEvaluationRuns = async (params: Record<string, any> = {}): Promise<any> => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.offset) queryParams.append('offset', params.offset);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.status) queryParams.append('status', params.status);
  if (params.datasetId) queryParams.append('dataset_id', params.datasetId);
  if (params.promptId) queryParams.append('prompt_id', params.promptId);
  if (params.days) queryParams.append('days', params.days);
  if (params.startDate) queryParams.append('start_date', params.startDate);
  if (params.endDate) queryParams.append('end_date', params.endDate);
  if (params.search) queryParams.append('search', params.search);

  const query = queryParams.toString();
  return fetchAPI(`/genaiops/evaluations${query ? '?' + query : ''}`);
};

export const getEvaluationRun = async (runId: string): Promise<any> => {
  return fetchAPI(`/genaiops/evaluations/${runId}`);
};

export const getEvaluationResults = async (runId: string, params: Record<string, any> = {}): Promise<any> => {
  const queryParams = new URLSearchParams();
  if (params.passedOnly) queryParams.append('passed_only', 'true');
  if (params.failedOnly) queryParams.append('failed_only', 'true');

  const query = queryParams.toString();
  return fetchAPI(`/genaiops/evaluations/${runId}/results${query ? '?' + query : ''}`);
};

export const checkEvaluationQualityGate = async (runId: string, qualityGate: Record<string, any>): Promise<any> => {
  return fetchAPI(`/genaiops/evaluations/${runId}/quality-gate`, {
    method: 'POST',
    body: JSON.stringify({
      min_pass_rate: qualityGate.minPassRate,
      min_avg_score: qualityGate.minAvgScore,
      max_avg_latency_ms: qualityGate.maxAvgLatencyMs,
      max_total_cost: qualityGate.maxTotalCost,
    }),
  });
};

export const deleteEvaluationRun = async (runId: string): Promise<any> => {
  return fetchAPI(`/genaiops/evaluations/${runId}`, {
    method: 'DELETE',
  });
};

export const assignEvaluationRunProject = async (runId: string, projectId: string | null): Promise<any> => {
  return fetchAPI(`/genaiops/evaluations/${runId}/project`, {
    method: 'PATCH',
    body: JSON.stringify({ project_id: projectId }),
  });
};

export const bulkAssignEvaluationRunProject = async (ids: string[], projectId: string | null): Promise<any> => {
  return fetchAPI('/genaiops/evaluations/bulk-assign-project', {
    method: 'PATCH',
    body: JSON.stringify({ ids, project_id: projectId }),
  });
};

export const assignEvaluationRunWorkload = async (runId: string, workloadId: string | null): Promise<any> => {
  return fetchAPI(`/genaiops/evaluations/${runId}/workload`, {
    method: 'PATCH',
    body: JSON.stringify({ workload_id: workloadId }),
  });
};

export const bulkAssignEvaluationRunWorkload = async (ids: string[], workloadId: string | null): Promise<any> => {
  return fetchAPI('/genaiops/evaluations/bulk-assign-workload', {
    method: 'PATCH',
    body: JSON.stringify({ ids, workload_id: workloadId }),
  });
};

// Type exports for TypeScript consumers
export interface RunEvaluationParams {
  datasetId: string;
  promptId?: string;
  provider: string;
  modelId: string;
  credentialId: string;
  scoringMethod: string;
  passThreshold: number;
  runAssertions: boolean;
  judgeRubricId?: string;
  [key: string]: unknown;
}
